import React, { Fragment, useMemo, useState } from 'react';
import { MenuItem } from '../MenuItem';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { useTheme } from '@mui/material';
import { ModuleMenu } from './components/ModuleMenu';
import { getActiveModuleBasedOnLocation, modules } from './helpers';
import {
  getModuleUsageCounts,
  recordModuleUsage,
  sortModulesByUsage,
} from './moduleUsage';
import { useLocation, useNavigate } from 'react-router';
import { IModuleMenuItem } from './components/ModuleMenuItem/interfaces';
import { useInDevMode } from '@hooks/useInDevMode';
import { usePermission } from '@hooks/usePermission';
import { IModuleSwitcher } from './interfaces';

export const ModuleSwitcher: React.FC<IModuleSwitcher> = ({
  onOptionSelected,
}): JSX.Element => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const inDevMode = useInDevMode();
  const { can } = usePermission();

  const [showModuleMenu, setShowModuleMenu] = useState<boolean>(false);
  const [usageRevision, setUsageRevision] = useState<number>(0);

  const defaultOrderById = useMemo(() => {
    const order = new Map<string, number>();
    modules.forEach((module, index) => {
      order.set(module.id, index);
    });

    return order;
  }, []);

  const activeModule = useMemo(
    () => getActiveModuleBasedOnLocation(location),
    [location]
  );

  const closeUserMenu = (): void => {
    setShowModuleMenu(false);
  };

  const onModuleSelectorClick = (): void => {
    setShowModuleMenu(true);
  };

  const onModuleClick = (module: IModuleMenuItem): void => {
    recordModuleUsage(module.id);
    setUsageRevision((revision) => revision + 1);

    if (!module.link) {
      alert('not implemented yet');
    } else {
      navigate(module.link);
    }

    onOptionSelected(module);
    closeUserMenu();
  };

  const activeModules = useMemo(() => {
    const filtered = modules.filter((module) => {
      if (module.inProduction && module.permission) {
        return can(module.permission);
      }

      if (inDevMode || module.inProduction) {
        return true;
      }

      return false;
    });

    return sortModulesByUsage(
      filtered,
      getModuleUsageCounts(),
      defaultOrderById
    );
  }, [can, inDevMode, defaultOrderById, usageRevision]);

  return (
    <Fragment>
      <MenuItem
        icon={activeModule.icon}
        postIcon={<UnfoldMoreIcon />}
        label={activeModule.title}
        onClick={onModuleSelectorClick}
        key="module-switcher"
        active={false}
        gutter
        sxButton={{
          backgroundColor: theme.palette.grey[100],
          border: `1px solid ${theme.palette.grey[300]}`,
          '&:hover': {
            backgroundColor: theme.palette.grey[200],
          },
        }}
      />
      <ModuleMenu
        show={!!showModuleMenu}
        onClose={closeUserMenu}
        onClick={onModuleClick}
        modules={activeModules}
        active={activeModule}
      />
    </Fragment>
  );
};
